import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { emotions_color } from '../../constants/Constants'
import { KIND_ACCENT, KIND_LABEL_KEYS, KindIcon } from './archiveKinds'
import { ArchiveViewerContext, MoodBody, PollBody, QuestionBody } from './ArchiveBodies'
import SurveyBody from './SurveyBody'
import JournalBody from './JournalBody'

const formatTime = (ms) => {
    try {
        return ms ? format(ms, 'HH:mm') : ''
    } catch (e) {
        return ''
    }
}

/**
 * One archived response. The frame (type, answered time, accent) is shared; the body
 * is picked by `item.kind` from the normalized shape GET /api/student/archive returns.
 */
function ArchiveItem({ item, index, openImageBig, onOpenDetail }) {
    const { t } = useTranslation()
    const viewer = useContext(ArchiveViewerContext)
    const labels = KIND_LABEL_KEYS[item.kind] || {}
    const label = t((item.content?.anonymous && labels.anonymous) || labels.open || item.kind)
    const accent = item.kind === 'mood' ? emotions_color[(item.response?.emotion || '').toLowerCase()] || '#BEBECE' : KIND_ACCENT[item.kind]
    const time = formatTime(item.created_date)

    let body = null
    switch (item.kind) {
        case 'mood':
            body = <MoodBody response={item.response || {}} time={time} />
            break
        case 'poll':
            body = <PollBody content={item.content} response={item.response || {}} openImageBig={openImageBig} />
            break
        case 'question':
            body = <QuestionBody content={item.content} response={item.response || {}} openImageBig={openImageBig} />
            break
        case 'survey':
            body = <SurveyBody content={item.content} response={item.response || {}} onOpen={() => onOpenDetail(item)} />
            break
        case 'journal':
            body = <JournalBody content={item.content} response={item.response || {}} onOpen={() => onOpenDetail(item)} answersHidden={viewer === 'staff'} />
            break
        default:
            return null
    }

    return (
        <article
            className={`archive-card archive-card--${item.kind} archive-card-enter`}
            // Only the first screenful staggers; later pages appear without waiting on a cascade.
            style={{ '--card-i': index < 7 ? index : 0, '--accent': accent }}
            aria-label={`${label}${time ? `, ${time}` : ''}`}
        >
            {/* Mood cards carry their own header row (face, prompt, time, emotion), like the original check-in card. */}
            {item.kind !== 'mood' && (
                <header className="archive-card-head">
                    <span className="archive-card-icon">
                        <KindIcon kind={item.kind} size={28} />
                    </span>
                    <span className="archive-card-type">{label}</span>
                    {!!time && (
                        <time className="archive-card-time" dateTime={new Date(item.created_date).toISOString()}>
                            {time}
                        </time>
                    )}
                </header>
            )}
            <div className="archive-card-body">{body}</div>
        </article>
    )
}

export default React.memo(ArchiveItem)
